import { useState, useEffect, useRef, useCallback } from 'react';
import logger from '@/utils/logger';
import { detectWakeWord, getMatchedWakeWords } from '@/utils/wakeWordDetector';

// SpeechRecognition 相关类型定义
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

// 自定义 SpeechRecognition 接口
interface ISpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

// 扩展全局类型定义
declare global {
  interface Window {
    SpeechRecognition: new () => ISpeechRecognition;
    webkitSpeechRecognition: new () => ISpeechRecognition;
  }
}

interface UseCloudSpeechRecognitionProps {
  onWakeWordDetected: () => void;
  wakeWords: string[];
  enabled: boolean;
  enableHomophone?: boolean;
}

interface UseCloudSpeechRecognitionReturn {
  status: string;
  error: string | null;
  start: () => Promise<void>;
  history: string[];
  partial: string;
}

export const useCloudSpeechRecognition = ({
  onWakeWordDetected,
  wakeWords,
  enabled,
  enableHomophone = true,
}: UseCloudSpeechRecognitionProps): UseCloudSpeechRecognitionReturn => {
  const MAX_HISTORY_ITEMS = 50;
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [partial, setPartial] = useState('');

  const recognitionRef = useRef<ISpeechRecognition | null>(null);
  // 取消标记：用于阻止卸载/禁用后仍自动重启
  const isCancelledRef = useRef(false);
  // 自动重启的定时器，显式记录并清理，避免残留引用导致内存上涨
  const restartTimerIdRef = useRef<number | null>(null);
  const onWakeWordDetectedRef = useRef(onWakeWordDetected);
  const wakeWordsRef = useRef(wakeWords); // 保持最新的唤醒词引用
  const enableHomophoneRef = useRef(enableHomophone); // 保持最新的同音字配置

  useEffect(() => {
    onWakeWordDetectedRef.current = onWakeWordDetected;
  }, [onWakeWordDetected]);

  // 保持 wakeWords 和 enableHomophone 的最新引用
  useEffect(() => {
    wakeWordsRef.current = wakeWords;
  }, [wakeWords]);

  useEffect(() => {
    enableHomophoneRef.current = enableHomophone;
  }, [enableHomophone]);

  useEffect(() => {
    isCancelledRef.current = false;
    if (!enabled) {
      setStatus('disabled');
      return;
    }

    // 检查浏览器支持
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setError('浏览器不支持语音识别');
      setStatus('error');
      return;
    }

    try {
      const recognition = new SpeechRecognition();

      // 配置识别器
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'zh-CN';
      recognition.maxAlternatives = 1;

      // 结果处理
      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let finalText = '';
        let interimText = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const transcript = result[0].transcript;

          if (result.isFinal) {
            finalText += transcript;
          } else {
            interimText += transcript;
          }
        }

        // 更新部分结果
        if (interimText) {
          setPartial(interimText);
          logger.debug('[CloudSpeech] Partial Result:', interimText);
        }

        // 处理最终结果
        if (finalText) {
          logger.debug('[CloudSpeech] Final Result:', finalText);
          setHistory((prev) => {
            const next = [...prev, finalText];
            if (next.length > MAX_HISTORY_ITEMS) next.splice(0, next.length - MAX_HISTORY_ITEMS);
            return next;
          });
          setPartial('');

          // 使用共享的唤醒词检测逻辑（通过 ref 获取最新值，避免闭包陷阱）
          const currentWakeWords = wakeWordsRef.current;
          const currentEnableHomophone = enableHomophoneRef.current;
          const isWakeWordDetected = detectWakeWord(finalText, currentWakeWords, currentEnableHomophone);
          if (isWakeWordDetected) {
            const matchedWords = getMatchedWakeWords(finalText, currentWakeWords, currentEnableHomophone);
            logger.info('[CloudSpeech] 唤醒词检测成功:', {
              原文: finalText,
              匹配的唤醒词: matchedWords,
              启用同音字: currentEnableHomophone,
              当前唤醒词列表: currentWakeWords,
            });
            onWakeWordDetectedRef.current();
          }
        }
      };

      // 错误处理
      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        logger.warn('[CloudSpeech] Recognition error:', event.error);
        setError(`识别错误: ${event.error}`);
        if (event.error === 'not-allowed') {
          setStatus('permission-denied');
        } else {
          setStatus('error');
        }
        // 出错时不再保留任何重启定时器
        if (restartTimerIdRef.current) {
          clearTimeout(restartTimerIdRef.current);
          restartTimerIdRef.current = null;
        }
      };

      // 状态事件
      recognition.onstart = () => {
        logger.info('[CloudSpeech] Recognition started');
        setStatus('listening');
        setError(null);
      };

      recognition.onend = () => {
        logger.debug('[CloudSpeech] Recognition ended');
        // 自动重启识别（需确保未被取消且仍启用）
        if (!isCancelledRef.current && enabled) {
          if (restartTimerIdRef.current) {
            clearTimeout(restartTimerIdRef.current);
          }
          restartTimerIdRef.current = window.setTimeout(() => {
            if (isCancelledRef.current || !enabled) return;
            try {
              recognition.start();
            } catch (e) {
              logger.warn('[CloudSpeech] Restart failed:', e);
            }
            // 清理已触发的定时器 id
            if (restartTimerIdRef.current) {
              clearTimeout(restartTimerIdRef.current);
              restartTimerIdRef.current = null;
            }
          }, 1000);
        }
      };

      recognitionRef.current = recognition;
      setStatus('ready');
    } catch (e) {
      logger.error('[CloudSpeech] Init error:', e);
      setError(String(e));
      setStatus('error');
    }

    return () => {
      isCancelledRef.current = true;
      if (restartTimerIdRef.current) {
        clearTimeout(restartTimerIdRef.current);
        restartTimerIdRef.current = null;
      }
      if (recognitionRef.current) {
        try {
          // 断开事件绑定，防止 onend 再次触发自动重启
          recognitionRef.current.onresult = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.onstart = null;
          recognitionRef.current.onend = null;
          recognitionRef.current.stop();
        } catch {}
        recognitionRef.current = null;
      }
    };
  }, [enabled, wakeWords, enableHomophone]);

  const start = useCallback(async () => {
    if (!enabled || !recognitionRef.current) {
      return;
    }

    try {
      // 请求麦克风权限
      const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // 停止临时音频流
      try {
        tempStream.getTracks().forEach((track) => track.stop());
      } catch {}

      // 启动识别
      recognitionRef.current.start();
      logger.info('[CloudSpeech] Started successfully');
    } catch (e) {
      logger.error('[CloudSpeech] Start error:', e);
      setError(`启动失败: ${e}`);
      setStatus('error');
    }
  }, [enabled]);

  return { status, error, start, history, partial };
};
